import { useCallback, useEffect, useState } from 'react';
import { isFreemiumEnabled } from '../utils/freemiumFlag';
import { getFreemiumStatus } from '../services/freemiumApi';

/**
 * useFreemiumLock — shared status for in-page freemium locks.
 *
 * enabled       — freemium funnel flag is on (frontend)
 * loading       — status fetch in flight (only true when enabled)
 * isFreeTier    — user's current/most-recent plan is FREE
 * isExhausted   — FREE tier's one interview has been used (entitlement not ACTIVE)
 * refresh       — re-fetch status (call after a successful Fast Track purchase)
 *
 * Fails open (isFreeTier=false) on error or when the flag is off, so a
 * backend hiccup never blocks access for non-free/existing users.
 */
export default function useFreemiumLock() {
  const enabled = isFreemiumEnabled();
  const [loading, setLoading] = useState(enabled);
  const [status, setStatus] = useState(null);

  const refresh = useCallback(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getFreemiumStatus()
      .then((data) => setStatus(data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFreeTier = Boolean(enabled && status?.is_free_tier);
  const isExhausted = isFreeTier && status?.entitlement_status !== 'ACTIVE';

  return { enabled, loading, isFreeTier, isExhausted, refresh };
}
